import { MouseEvent as ReactMouseEvent } from 'react'
import { Pager, PagerNextButton, PagerPreviousButton, PagerProps, PagerText } from '~/components/v2/Pager'
import { useMsg } from '~/common/localizationUtils'

/**
 * @deprecated This component has been replaced by our new Pager components as part of the DS,
 * please refer to https://storybook.beta.vndly.com/?path=/docs/components-navigation-pagination--docs
 */
export type CountPagerProps = {
  /** The current page number. */
  page?: number
  /** The total number of pages. */
  totalPages?: number
  /** The total number of results. */
  totalResults?: number
  /** Callback for previous page button click. */
  onPrevPage: (event: ReactMouseEvent<any, MouseEvent>) => void
  /** Callback for next page button click. */
  onNextPage: (event: ReactMouseEvent<any, MouseEvent>) => void
  pageNumberTestId?: string
}

/**
 * @deprecated This component has been replaced by our new Pager components as part of the DS,
 * please refer to https://storybook.beta.vndly.com/?path=/docs/components-navigation-pagination--docs
 */
export const CountPager = ({
  page = 0,
  totalPages = 0,
  totalResults = 0,
  onPrevPage,
  onNextPage,
  pageNumberTestId,
  ...props
}: CountPagerProps & Omit<PagerProps, 'onChange'>) => {
  const msg = useMsg()

  return (
    <Pager {...props}>
      <PagerPreviousButton isDisabled={page === 1 || totalPages === 0} onClick={onPrevPage} />
      <PagerText data-testid={pageNumberTestId}>
        {totalPages > 0 &&
          msg('pager.pages.text', {
            page: Number(page).toLocaleString('en'),
            totalPages: Number(totalPages).toLocaleString('en')
          })}
        {totalResults > 0 &&
          ` ${msg('pager.with_results.text', {
            totalResults: Number(totalResults).toLocaleString('en')
          })}`}
      </PagerText>
      <PagerNextButton isDisabled={page === totalPages || totalPages === 0} onClick={onNextPage} />
    </Pager>
  )
}

/**
 * Custom Pagination logic for card view pages with action card:
 * On adding the "Action" card the api pagination results need modification.
 * As the first page will have a maximum of PAGE_LIMIT-1 cards & one "Action" card
 * Rest of the pages will have a maximum of PAGE_LIMIT cards & no "Action" card
 *
 * @example
 * Total number of results = 27
 * Assuming PAGE_LIMIT is 9, It should result in 3 pages (3 x 9)
 * But because first card is the action card, it results in 4 pages
 * Page 1 -> 1 Action Card + 8 cards
 * Page 2 -> 9 cards
 * Page 3 -> 9 cards
 * Page 4 -> 1 card
 */
export function getCardViewPaginationParams({
  offset,
  pageLimit,
  firstPageLimit: _firstPageLimit,
  totalResults
}: {
  offset: number
  pageLimit: number
  firstPageLimit?: number
  totalResults: number
}) {
  const firstPageLimit = _firstPageLimit ?? pageLimit - 1
  const nextPageParams =
    offset === 0 ? { offset: firstPageLimit, limit: pageLimit } : { offset: offset + pageLimit, limit: pageLimit }
  const previousPageParams =
    offset === firstPageLimit ? { offset: 0, limit: firstPageLimit } : { offset: offset - pageLimit, limit: pageLimit }

  function getTotalPages() {
    const countAfterFirstPage = totalResults - firstPageLimit
    if (countAfterFirstPage < 0) {
      return 1
    }
    const remainingPages = Math.ceil(countAfterFirstPage / pageLimit)
    return 1 + remainingPages
  }

  return {
    page: Math.floor((offset + 1) / pageLimit + 1),
    nextPageParams,
    previousPageParams,
    totalPages: getTotalPages()
  }
}
